import { LabelComponent } from "../labels/labelComponents"
import { formatPartialOrderHash } from "../../utils/formatters"
import { Link, useLocation } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { useContext, useEffect, useState } from "react"
import { OrderDetailsContext } from "../../context/OrderDetailContext"
import { PaidState } from "../../types/order/paidState"
import { LogisticState } from "../../types/order/logisticState"
import { dictionary } from "../../utils/dictionary"
import { useAuth } from "../../context/AuthContext"
import { ConfirmationInfo, getConfirmationStatuses } from "../../services/whatsapp/whatsappService"
import { decodeTokenService } from "../../services/decodeTokenService"
import { handleApi } from "../../utils/handleApi"

const CONFIRMATION_LABEL_CONFIG: Record<string, { bg: string; color: string; label: Record<string, string> }> = {
  pending: { bg: "bg-gray-100", color: "text-gray-800", label: { en: "Pending", es: "Pendiente", pt: "Pendente" } },
  awaiting_response: { bg: "bg-amber-100", color: "text-amber-800", label: { en: "Awaiting", es: "Esperando", pt: "Aguardando" } },
  confirmed: { bg: "bg-emerald-100", color: "text-emerald-800", label: { en: "Confirmed", es: "Confirmado", pt: "Confirmado" } },
  rejected: { bg: "bg-red-100", color: "text-red-800", label: { en: "Rejected", es: "Rechazado", pt: "Rejeitado" } },
  expired: { bg: "bg-gray-100", color: "text-gray-600", label: { en: "Expired", es: "Expirado", pt: "Expirado" } },
  canceled: { bg: "bg-gray-100", color: "text-gray-600", label: { en: "Canceled", es: "Cancelado", pt: "Cancelado" } },
  send_failed: { bg: "bg-red-100", color: "text-red-800", label: { en: "Send failed", es: "Error de envío", pt: "Erro de envio" } },
};

export const TopNavBarComponent = () => {
    const context = useContext(OrderDetailsContext);
    const { language } = useAuth();
    const [confirmationInfo, setConfirmationInfo] = useState<ConfirmationInfo | null>(null);

    useEffect(() => {
      if (!context?.orderDetails?.id) return;
      const ownerId = decodeTokenService.getUserIdFromToken();
      if (!ownerId) return;

      const fetchStatus = async () => {
        const { res, error } = await handleApi(
          getConfirmationStatuses(ownerId, [context.orderDetails!.id]),
          false
        );
        if (!error && res) {
          const info = res[String(context.orderDetails!.id)];
          if (info) setConfirmationInfo(info);
        }
      };
      fetchStatus();
    }, [context?.orderDetails?.id]);

    if (!context || !context.orderDetails) return null;

    const location = useLocation();
    const { orderDetails: order } = context;

    const langKey = language === "en" ? "en" : language === "pt" ? "pt" : "es";
    const statusConfig = confirmationInfo ? CONFIRMATION_LABEL_CONFIG[confirmationInfo.status] : null;

    return (
      <div className="bg-white p-6 flex flex-row gap-4 items-center">
        <Link to={`/orders${location.search}`} className="flex items-center text-gray-600 hover:text-gray-900 mr-4">
          <ArrowLeft size={20} className="mr-2" />
        </Link>
        <div className="flex flex-row gap-2 items-center flex-wrap">
          <p>
            {dictionary["order-details"][language]} <span className="pl-2">{"  "}</span>#
            {formatPartialOrderHash(order?.hash_id?.toString() || "")}
          </p>
          {order.payment_state ? (
            order.payment_state === PaidState.PAID ? (
              <div className="flex items-center">
                <span className="flex items-center px-3 py-1 text-sm rounded-full bg-green-100 text-green-800">
                  <span className="w-2 h-2 mr-2 bg-green-500 rounded-full"></span>
                  {dictionary["paid-state"][language]}
                </span>
              </div>
            ) : (
              <div className="flex items-center">
                <span className="flex items-center px-3 py-1 text-sm rounded-full bg-red-100 text-red-800">
                  <span className="w-2 h-2 mr-2 bg-red-500 rounded-full"></span>
                  {order.payment_state === LogisticState.CREATED
                    ? dictionary["unpaid-state"][language]
                    : dictionary["pending-state"][language]}
                </span>
              </div>
            )
          ) : null}
          <LabelComponent
            text={order?.logistic_state === LogisticState.IN_PROGRESS ? dictionary["fulfilled-state"][language] : dictionary["unfulfilled-state"][language]}
            bg={order?.logistic_state === LogisticState.IN_PROGRESS ? "bg-blue-100" : "bg-yellow-100"}
            color={order?.logistic_state === LogisticState.IN_PROGRESS ? "text-blue-800" : "text-yellow-800"}
          />
          <LabelComponent text={dictionary["priority"][language]} bg="bg-violet-100" color="text-violet-800" />
          {statusConfig && (
            <LabelComponent
              text={statusConfig.label[langKey]}
              bg={statusConfig.bg}
              color={statusConfig.color}
            />
          )}
        </div>
      </div>
    );
  };
